import React from 'react';

import { FormApi } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { DocumentFile } from '../../../types/DocumentFile';
import { formatNumber } from '../../../helpers/ChartHelper';
import DescriptionIcon from '@material-ui/icons/Description';
import { DocumentsDropzone } from './DocumentsDropzone';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('DocumentsPanel');

// Explicit request (EnquiryFormDrawer split): the "Documents" accordion (file upload / list).
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface DocumentsPanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    countValidFiles: (data: DocumentFile[]) => number;
    onFileClicked: (file: DocumentFile) => void;
    generateUniqueFileName: (name: string, existing: DocumentFile[]) => string;
    setSnackbar: (snackbar: { open: boolean; message: string | null; severity?: 'success' | 'error' }) => void;
}

const DocumentsPanelBody: React.FC<DocumentsPanelProps> = React.memo((bodyProps) => {
    const {
        classes, countValidFiles, onFileClicked, generateUniqueFileName, setSnackbar,
    } = bodyProps;

    return (
        <Accordion
            data-tour="enquiry-documents"
            className={classes.expansionPanel}
            defaultExpanded={false}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="documents-content"
                id="documents-header"
                className={classes.expansionPanelSummary}
            >
                <div className={classes.sectionHeaderRow}>
                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipIndigo)}>
                        <DescriptionIcon fontSize="small" />
                    </span>
                    <Typography className={clsx(classes.heading, classes.sectionLabelIndigo, classes.sectionLabelText)}>
                        Documents
                    </Typography>
                </div>
                <div className={classes.infoContainer}>
                    <div className="col">
                        <Field name="files">
                            {(
                                { input } /* FieldRenderProps */
                            ) => {

                                const files: DocumentFile[] = input.value;

                                return (
                                    <div className="value files">
                                        {countValidFiles(files) > 0 && `${formatNumber(files.length)} file(s)`}
                                    </div>
                                );
                            }}
                        </Field>
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid
                        item={true}
                        xs={12}
                        className={clsx(classes.customFieldGrid, classes.fieldRowGap)}
                    >
                        <Field name="files">
                            {({ input }) => (
                                <DocumentsDropzone
                                    value={input.value || []}
                                    onChange={input.onChange}
                                    onFileClicked={onFileClicked}
                                    generateUniqueFileName={generateUniqueFileName}
                                    onNotify={(message, severity) => {
                                        setSnackbar({ open: true, message, severity });
                                    }}
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
DocumentsPanelBody.displayName = 'DocumentsPanelBody';

export const DocumentsPanel: React.FC<DocumentsPanelProps> = DocumentsPanelBody;
